import React, { useState, useEffect, useMemo } from 'react';
import { formatISO, parseISO, format } from 'date-fns';
import {
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  Alert,
  Chip
} from '@mui/material';
import AdminButton from 'components/common/AdminButton';
import { Edit as EditIcon, Save as SaveIcon, Cancel as CancelIcon } from '@mui/icons-material';
import { API, graphqlOperation } from 'aws-amplify';
import { locationLaborTargetsByDate } from 'graphql/queries';
import { createLocationLaborTargets, updateLocationLaborTargets } from 'graphql/mutations';
import { useUser } from 'components/RDAuthContext';
import { laborService } from '../services/domain'; // UPDATED: Using laborService
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper
} from '@tanstack/react-table';

function LaborTargetsTab({ location, onUpdate }) {
  const { user } = useUser();
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [laborTargets, setLaborTargets] = useState(null);
  const [editedTargets, setEditedTargets] = useState(null);
  const [error, setError] = useState(null);
  const [changeReason, setChangeReason] = useState('');
  
  // Compute user display name once
  const userDisplayName = user?.firstName && user?.lastName 
    ? `${user.firstName} ${user.lastName}` 
    : user?.userName || user?.email || 'Unknown User';

  const getEmptyTargets = () => ({
    fohBaseHours: {
      Mon: 0, Tue: 0, Wed: 0, Thu: 0, Fri: 0, Sat: 0, Sun: 0
    },
    bohBaseHours: {
      Mon: 0, Tue: 0, Wed: 0, Thu: 0, Fri: 0, Sat: 0, Sun: 0
    },
    fohSplhTargets: {
      Mon: 0, Tue: 0, Wed: 0, Thu: 0, Fri: 0, Sat: 0, Sun: 0
    },
    bohSplhTargets: {
      Mon: 0, Tue: 0, Wed: 0, Thu: 0, Fri: 0, Sat: 0, Sun: 0
    }
  });

  useEffect(() => {
    const fetchLaborTargets = async () => {
      try {
        setLoading(true);
        setError(null);

        // First try to get active records
        let result = await API.graphql(
          graphqlOperation(locationLaborTargetsByDate, {
            locationId: location.id,
            filter: { isActive: { eq: true } },
            sortDirection: 'DESC',
            limit: 1
          })
        );

        let targets = result.data?.locationLaborTargetsByDate?.items?.[0];
        
        // If no active records found, check if there are any inactive ones (might be from failed save)
        if (!targets) {
          result = await API.graphql(
            graphqlOperation(locationLaborTargetsByDate, {
              locationId: location.id,
              sortDirection: 'DESC',
              limit: 1
            })
          );

          targets = result.data?.locationLaborTargetsByDate?.items?.[0];
          
          if (targets && !targets.isActive) {
            // Reactivate it temporarily for display/editing
            // This allows the user to see their data and try saving again
            targets = { ...targets, isActive: true };
            setError('Found inactive labor targets (possibly from a failed save). Please review and save again.');
          }
        }

        setLaborTargets(targets);
        setEditedTargets(targets || getEmptyTargets());

      } catch (err) {
        console.error('Error fetching labor targets:', err);
        setError('Failed to load labor targets');
        setEditedTargets(getEmptyTargets());
      } finally {
        setLoading(false);
      }
    };

    if (location?.id) {
      fetchLaborTargets();
    }
  }, [location?.id]);

  const cleanTargetData = (data) => {
    // Remove __typename and other non-input fields from nested objects
    // Also ensure all values are proper floats
    const cleaned = {};
    
    if (data.fohBaseHours) {
      cleaned.fohBaseHours = {
        Mon: parseFloat(data.fohBaseHours.Mon) || 0,
        Tue: parseFloat(data.fohBaseHours.Tue) || 0,
        Wed: parseFloat(data.fohBaseHours.Wed) || 0,
        Thu: parseFloat(data.fohBaseHours.Thu) || 0,
        Fri: parseFloat(data.fohBaseHours.Fri) || 0,
        Sat: parseFloat(data.fohBaseHours.Sat) || 0,
        Sun: parseFloat(data.fohBaseHours.Sun) || 0
      };
    }
    
    if (data.bohBaseHours) {
      cleaned.bohBaseHours = {
        Mon: parseFloat(data.bohBaseHours.Mon) || 0,
        Tue: parseFloat(data.bohBaseHours.Tue) || 0,
        Wed: parseFloat(data.bohBaseHours.Wed) || 0,
        Thu: parseFloat(data.bohBaseHours.Thu) || 0,
        Fri: parseFloat(data.bohBaseHours.Fri) || 0,
        Sat: parseFloat(data.bohBaseHours.Sat) || 0,
        Sun: parseFloat(data.bohBaseHours.Sun) || 0
      };
    }
    
    if (data.fohSplhTargets) {
      cleaned.fohSplhTargets = {
        Mon: parseFloat(data.fohSplhTargets.Mon) || 0,
        Tue: parseFloat(data.fohSplhTargets.Tue) || 0,
        Wed: parseFloat(data.fohSplhTargets.Wed) || 0,
        Thu: parseFloat(data.fohSplhTargets.Thu) || 0,
        Fri: parseFloat(data.fohSplhTargets.Fri) || 0,
        Sat: parseFloat(data.fohSplhTargets.Sat) || 0,
        Sun: parseFloat(data.fohSplhTargets.Sun) || 0
      };
    }
    
    if (data.bohSplhTargets) {
      cleaned.bohSplhTargets = {
        Mon: parseFloat(data.bohSplhTargets.Mon) || 0,
        Tue: parseFloat(data.bohSplhTargets.Tue) || 0,
        Wed: parseFloat(data.bohSplhTargets.Wed) || 0,
        Thu: parseFloat(data.bohSplhTargets.Thu) || 0,
        Fri: parseFloat(data.bohSplhTargets.Fri) || 0,
        Sat: parseFloat(data.bohSplhTargets.Sat) || 0,
        Sun: parseFloat(data.bohSplhTargets.Sun) || 0
      };
    }
    
    return cleaned;
  };

  const handleEdit = () => {
    setEditMode(true);
    setChangeReason(''); // Clear reason when starting edit
    const targetsToEdit = laborTargets ? cleanTargetData(laborTargets) : getEmptyTargets();
    setEditedTargets(targetsToEdit);
  };

  const handleCancel = () => {
    setEditMode(false);
    setChangeReason(''); // Clear reason when canceling
    setEditedTargets(laborTargets);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      setError(null);

      // Clean the edited targets to ensure no __typename fields
      const cleanedTargets = cleanTargetData(editedTargets);

      // Validate and build the targets data
      const userId = user.id || user.userName || user.email || 'unknown';
      const currentDateTime = formatISO(new Date());

      const targetsData = {
        clientId: user.client || user.clientId || 'grm',
        locationId: location.id,
        fohBaseHours: cleanedTargets.fohBaseHours || getEmptyTargets().fohBaseHours,
        bohBaseHours: cleanedTargets.bohBaseHours || getEmptyTargets().bohBaseHours,
        fohSplhTargets: cleanedTargets.fohSplhTargets || getEmptyTargets().fohSplhTargets,
        bohSplhTargets: cleanedTargets.bohSplhTargets || getEmptyTargets().bohSplhTargets,
        effectiveDate: currentDateTime,
        lastModified: currentDateTime,  // Add the missing required field
        isActive: true,
        reason: changeReason.trim() || 'Manual update via Location Admin',  // Use provided reason or default
        reasonType: 'ADJUSTMENT',
        changedBy: userId
        // Note: changedByName is not in the input type, will be set by resolver or separately
      };

      // Save to database first
      let newLaborTargets;
      if (laborTargets?.id) {
        // IMPORTANT: Create new record FIRST, before deactivating old one
        // Make sure all required fields are present and no undefined values
        const createInput = {
          clientId: targetsData.clientId,
          locationId: targetsData.locationId,
          fohBaseHours: targetsData.fohBaseHours,
          bohBaseHours: targetsData.bohBaseHours,
          fohSplhTargets: targetsData.fohSplhTargets,
          bohSplhTargets: targetsData.bohSplhTargets,
          effectiveDate: targetsData.effectiveDate,
          lastModified: targetsData.lastModified,  // Include the required lastModified field
          isActive: targetsData.isActive,
          reason: targetsData.reason,
          reasonType: targetsData.reasonType,
          changedBy: targetsData.changedBy,
          supersedes: laborTargets.id
          // Note: endDate is intentionally not included for new active records
          // Note: changedByName is not in the input type, would need separate update
        };

        let newRecord;
        try {
          // Step 1: Try to create the new record first

          const mutationCall = graphqlOperation(createLocationLaborTargets, {
            input: createInput
          });

          newRecord = await API.graphql(mutationCall);

          // Step 2: Only if creation succeeded, deactivate the old record

          try {
            const updateInput = {
              id: laborTargets.id,
              isActive: false,
              endDate: formatISO(new Date()),
              supersededBy: newRecord.data.createLocationLaborTargets.id
            };

            await API.graphql(
              graphqlOperation(updateLocationLaborTargets, {
                input: updateInput
              })
            );

          } catch (updateError) {
            console.error('⚠️ Warning: Failed to deactivate old record:', updateError);
            console.error('The new record was created but old record remains active');
            // Don't throw - the main save operation succeeded
          }
          
        } catch (createError) {
          console.error('Error in createLocationLaborTargets mutation:', createError);
          console.error('Mutation input that failed:', createInput);
          console.error('IMPORTANT: Old record was NOT deactivated, data remains unchanged');
          throw createError;
        }

        newLaborTargets = newRecord.data.createLocationLaborTargets;
      } else {
        // Create first record

        const newRecord = await API.graphql(
          graphqlOperation(createLocationLaborTargets, {
            input: targetsData
          })
        );
        newLaborTargets = newRecord.data.createLocationLaborTargets;
      }

      // Log the change to audit history
      try {
        const previousClean = laborTargets ? cleanTargetData(laborTargets) : getEmptyTargets();

        const auditResult = await laborService.updateLocationLaborTargets(
          location.id,
          {
            locationName: location.locationName,
            previousTargets: previousClean,
            currentTargets: cleanedTargets,
            reason: changeReason.trim() || 'Manual update via Location Admin',  // Use the same reason
            reasonType: 'ADJUSTMENT'
          },
          user.userName || user.id,
          `${user.firstName} ${user.lastName}` || user.userName || user.email,
          user.clientId || user.client || 'grm'
        );

      } catch (auditError) {
        console.error('Failed to create audit log for labor targets update:', auditError);
        // Continue - audit logging failure shouldn't prevent the save
      }

      setLaborTargets(newLaborTargets);
      setEditMode(false);
      setChangeReason(''); // Clear reason after successful save
      
      if (onUpdate) {
        onUpdate({ ...location, laborTargets: editedTargets });
      }
    } catch (err) {
      console.error('Error saving labor targets:', err);
      console.error('Full error details:', JSON.stringify(err, null, 2));
      if (err.errors) {
        console.error('GraphQL errors:', err.errors);
      }
      setError('Failed to save labor targets');
    } finally {
      setLoading(false);
    }
  };

  const handleValueChange = (category, day, value) => {
    setEditedTargets(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [day]: value === '' ? 0 : value
      }
    }));
  };

  // Create column helper
  const columnHelper = createColumnHelper();

  // Create columns for base hours table (transposed)
  const baseHoursColumns = useMemo(() => [
    columnHelper.accessor('day', {
      header: 'Day',
      cell: info => info.getValue()
    }),
    columnHelper.accessor('fohHours', {
      header: 'FOH',
      cell: info => info.getValue()
    }),
    columnHelper.accessor('bohHours', {
      header: 'BOH', 
      cell: info => info.getValue()
    })
  ], [columnHelper]);

  // Create columns for SPLH targets table (transposed)
  const splhColumns = useMemo(() => [
    columnHelper.accessor('day', {
      header: 'Day',
      cell: info => info.getValue()
    }),
    columnHelper.accessor('fohSplh', {
      header: 'FOH',
      cell: info => info.getValue()
    }),
    columnHelper.accessor('bohSplh', {
      header: 'BOH',
      cell: info => info.getValue()
    })
  ], [columnHelper]);

  // Data for base hours table (days as rows)
  const baseHoursData = useMemo(() => {
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    return days.map(day => ({
      day,
      fohHours: editedTargets?.fohBaseHours?.[day] || 0,
      bohHours: editedTargets?.bohBaseHours?.[day] || 0
    }));
  }, [editedTargets]);

  // Data for SPLH targets table (days as rows)
  const splhData = useMemo(() => {
    const days = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
    return days.map(day => ({
      day,
      fohSplh: editedTargets?.fohSplhTargets?.[day] || 0,
      bohSplh: editedTargets?.bohSplhTargets?.[day] || 0
    }));
  }, [editedTargets]);

  // Create tables
  const baseHoursTable = useReactTable({
    data: baseHoursData,
    columns: baseHoursColumns,
    getCoreRowModel: getCoreRowModel()
  });

  const splhTable = useReactTable({
    data: splhData,
    columns: splhColumns,
    getCoreRowModel: getCoreRowModel()
  });

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: editMode ? 2 : 0 }}>
          <Typography variant="h6">Labor Targets Configuration</Typography>
          <Box>
            {editMode ? (
              <>
                <AdminButton
                  startIcon={<SaveIcon />}
                  variant="contained"
                  onClick={handleSave}
                  size="small"
                  sx={{ mr: 1 }}
                >
                  Save
                </AdminButton>
                <AdminButton
                  startIcon={<CancelIcon />}
                  variant="outlined"
                  onClick={handleCancel}
                  size="small"
                >
                  Cancel
                </AdminButton>
              </>
            ) : (
              <IconButton
                onClick={handleEdit}
                size="small"
                sx={{ 
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
        </Box>
        
        {/* Reason for change field - only visible in edit mode */}
        {editMode && (
          <TextField
            fullWidth
            label="Reason for change"
            placeholder="Enter reason for making these changes (optional)"
            value={changeReason}
            onChange={(e) => setChangeReason(e.target.value)}
            size="small"
            variant="outlined"
            sx={{ 
              backgroundColor: 'background.paper',
              '& .MuiOutlinedInput-root': {
                fontSize: '0.875rem'
              }
            }}
          />
        )}
      </Box>

      {laborTargets && (
        <Box sx={{ mb: 2 }}>
          <Chip 
            label={`Last updated: ${format(parseISO(laborTargets.effectiveDate), 'P')}`}
            size="small"
            variant="outlined"
            sx={{ mr: 1 }}
          />
          {(laborTargets.changedByName || laborTargets.changedBy) && (
            <Chip 
              label={`By: ${laborTargets.changedByName || userDisplayName || laborTargets.changedBy}`}
              size="small"
              variant="outlined"
            />
          )}
        </Box>
      )}

      <Grid container spacing={2}>
        {/* Base Hours Table */}
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight="600" gutterBottom>
              Base Hours
            </Typography>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                {baseHoursTable.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <th 
                        key={header.id}
                        style={{
                          padding: '6px 8px',
                          textAlign: 'left',
                          borderBottom: '2px solid #e0e0e0',
                          fontWeight: 600,
                          backgroundColor: '#f5f5f5',
                          fontSize: '0.813rem'
                        }}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {baseHoursTable.getRowModel().rows.map(row => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map(cell => {
                      const columnId = cell.column.id;
                      const day = row.original.day;
                      
                      return (
                        <td 
                          key={cell.id}
                          style={{
                            padding: '4px 8px',
                            borderBottom: '1px solid #e0e0e0',
                            fontSize: '0.875rem'
                          }}
                        >
                          {(() => {
                            if (columnId === 'day') {
                              return day;
                            }
                            
                            if (editMode) {
                              if (columnId === 'fohHours') {
                                return (
                                  <TextField
                                    size="small"
                                    type="number"
                                    value={editedTargets?.fohBaseHours?.[day] || 0}
                                    onChange={(e) => handleValueChange('fohBaseHours', day, e.target.value)}
                                    onBlur={(e) => {
                                      const numValue = parseFloat(e.target.value) || 0;
                                      handleValueChange('fohBaseHours', day, numValue);
                                    }}
                                    inputProps={{ step: 0.5, min: 0, style: { fontSize: '0.875rem', padding: '4px 8px' } }}
                                    sx={{ width: 80 }}
                                  />
                                );
                              }
                              if (columnId === 'bohHours') {
                                return (
                                  <TextField
                                    size="small"
                                    type="number"
                                    value={editedTargets?.bohBaseHours?.[day] || 0}
                                    onChange={(e) => handleValueChange('bohBaseHours', day, e.target.value)}
                                    onBlur={(e) => {
                                      const numValue = parseFloat(e.target.value) || 0;
                                      handleValueChange('bohBaseHours', day, numValue);
                                    }}
                                    inputProps={{ step: 0.5, min: 0, style: { fontSize: '0.875rem', padding: '4px 8px' } }}
                                    sx={{ width: 80 }}
                                  />
                                );
                              }
                            }
                            
                            // Non-edit mode
                            if (columnId === 'fohHours') {
                              return editedTargets?.fohBaseHours?.[day] || 0;
                            }
                            if (columnId === 'bohHours') {
                              return editedTargets?.bohBaseHours?.[day] || 0;
                            }
                            
                            return flexRender(cell.column.columnDef.cell, cell.getContext());
                          })()}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </Paper>
        </Grid>

        {/* SPLH Targets Table */}
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight="600" gutterBottom>
              SPLH Targets
            </Typography>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
              <thead>
                {splhTable.getHeaderGroups().map(headerGroup => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <th 
                        key={header.id}
                        style={{
                          padding: '6px 8px',
                          textAlign: 'left',
                          borderBottom: '2px solid #e0e0e0',
                          fontWeight: 600,
                          backgroundColor: '#f5f5f5',
                          fontSize: '0.813rem'
                        }}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {splhTable.getRowModel().rows.map(row => (
                  <tr key={row.id}>
                    {row.getVisibleCells().map(cell => {
                      const columnId = cell.column.id;
                      const day = row.original.day;
                      
                      return (
                        <td 
                          key={cell.id}
                          style={{
                            padding: '4px 8px',
                            borderBottom: '1px solid #e0e0e0',
                            fontSize: '0.875rem'
                          }}
                        >
                          {(() => {
                            if (columnId === 'day') {
                              return day;
                            }
                            
                            if (editMode) {
                              if (columnId === 'fohSplh') {
                                return (
                                  <TextField
                                    size="small"
                                    type="number"
                                    value={editedTargets?.fohSplhTargets?.[day] || 0}
                                    onChange={(e) => handleValueChange('fohSplhTargets', day, e.target.value)}
                                    onBlur={(e) => {
                                      const numValue = parseFloat(e.target.value) || 0;
                                      handleValueChange('fohSplhTargets', day, numValue);
                                    }}
                                    inputProps={{ step: 0.1, min: 0, style: { fontSize: '0.875rem', padding: '4px 8px' } }}
                                    sx={{ width: 80 }}
                                  />
                                );
                              }
                              if (columnId === 'bohSplh') {
                                return (
                                  <TextField
                                    size="small"
                                    type="number"
                                    value={editedTargets?.bohSplhTargets?.[day] || 0}
                                    onChange={(e) => handleValueChange('bohSplhTargets', day, e.target.value)}
                                    onBlur={(e) => {
                                      const numValue = parseFloat(e.target.value) || 0;
                                      handleValueChange('bohSplhTargets', day, numValue);
                                    }}
                                    inputProps={{ step: 0.1, min: 0, style: { fontSize: '0.875rem', padding: '4px 8px' } }}
                                    sx={{ width: 80 }}
                                  />
                                );
                              }
                            }
                            
                            // Non-edit mode
                            if (columnId === 'fohSplh') {
                              return editedTargets?.fohSplhTargets?.[day] || 0;
                            }
                            if (columnId === 'bohSplh') {
                              return editedTargets?.bohSplhTargets?.[day] || 0;
                            }
                            
                            return flexRender(cell.column.columnDef.cell, cell.getContext());
                          })()}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default LaborTargetsTab;